import React from 'react';
import { Package, Briefcase, FolderOpen, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import { buildDashboardCharts, withKpiVariants } from '../../shared/components/dashboard/moduleDashboardCharts';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';

const NS = 'modules.article.pages.dashboardPage';

const BREAKDOWN_LABEL_KEYS = {
  products: 'chart_products',
  services: 'chart_services',
  variants: 'chart_variants',
};

export default function ArticlesDashboardPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);

  return (
    <ModernModuleDashboard
      apiEndpoint="/articles/dashboard"
      title={p('title', { defaultValue: 'لوحة تحكم الأصناف' })}
      subtitle={p('subtitle', { defaultValue: 'نظرة عامة على كتالوج المنتجات والخدمات' })}
      icon={Package}
      accent="from-emerald-600 to-emerald-800"
      recentTitle={p('recent_additions', { defaultValue: 'آخر الإضافات' })}
      quickLinks={[
        { label: p('link_products', { defaultValue: 'المنتجات' }), href: '/products', icon: Package },
        { label: p('link_services', { defaultValue: 'الخدمات' }), href: '/services', icon: Briefcase },
        { label: p('link_categories', { defaultValue: 'الفئات' }), href: '/categories', icon: FolderOpen },
        { label: p('link_analytics', { defaultValue: 'التحليلات' }), href: '/articles/analyses-rapports', icon: BarChart3 },
      ]}
      mapData={(data) => {
        const kpis = data.kpis || {};
        const activeCount = kpis.activeItems ?? ((kpis.activeProducts || 0) + (kpis.activeServices || 0));
        const totalItems = kpis.totalItems ?? ((kpis.totalProducts || 0) + (kpis.totalServices || 0));
        const inactiveCount = kpis.inactiveItems ?? Math.max(0, totalItems - activeCount);

        const catalogBreakdown = (data.catalogBreakdown || []).map((row) => ({
          label: p(BREAKDOWN_LABEL_KEYS[row.label] || row.label, { defaultValue: row.label }),
          value: row.value,
        }));

        return {
          hero: {
            label: p('kpi_catalog_value', { defaultValue: 'قيمة الكتالوج' }),
            value: formatBrandMoney(kpis.catalogValue || 0),
            icon: Package,
            sub: p('count_total_items', { count: totalItems, defaultValue: '{{count}} عنصر' }),
          },
          kpis: withKpiVariants([
            { key: 'totalItems', label: p('kpi_total_items'), value: totalItems, icon: Package },
            { key: 'active', label: p('kpi_active'), value: activeCount, icon: Briefcase },
            { key: 'inactive', label: p('kpi_inactive'), value: inactiveCount, icon: FolderOpen },
            { key: 'categories', label: p('kpi_categories'), value: kpis.totalCategories ?? 0, icon: BarChart3 },
          ]),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_catalog_title', { defaultValue: 'توزيع الكتالوج' }),
              subtitle: p('chart_catalog_subtitle', { defaultValue: 'توزيع عناصر الكتالوج' }),
              rows: catalogBreakdown,
            },
            status: {
              title: p('chart_status_title', { defaultValue: 'حالة الأصناف' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'نشط مقابل غير نشط' }),
              active: activeCount,
              inactive: inactiveCount,
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_category_title', { defaultValue: 'المنتجات حسب الفئة' }),
              subtitle: p('chart_category_subtitle', { defaultValue: 'أكثر الفئات استخداماً' }),
              rows: data.chartRows || [],
            },
          }),
          recentItems: data.recentItems || [],
        };
      }}
    />
  );
}
